import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Upload, X, Lock, ImagePlus } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { PageShell } from "@/components/PageShell";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

export default function Gallery() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [showUpload, setShowUpload] = useState(false);

  const load = () => api.get("/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const years = [...new Set(items.map((i) => i.year))].sort((a, b) => b - a);

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Camera className="text-sky-600" />
              <h1 className="font-display text-4xl lg:text-5xl text-slate-900">Gallery</h1>
            </div>
            <p className="mt-3 text-slate-600 text-lg">Moments from the Classic and good times with Pow.</p>
          </div>

          {user ? (
            <button data-testid="open-upload-btn" onClick={() => setShowUpload(true)} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-600 text-white font-semibold shadow-md hover:-translate-y-0.5 transition-all">
              <Upload size={18} /> Add a photo
            </button>
          ) : (
            <Link to="/login" data-testid="upload-login-prompt" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-50 text-sky-700 font-semibold hover:-translate-y-0.5 transition-all">
              <Lock size={16} /> Sign in to add photos
            </Link>
          )}
        </div>

        {years.map((year) => (
          <section key={year} className="mt-12">
            <h2 className="font-display text-2xl text-slate-800 mb-5">{year}</h2>
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [&>*]:mb-4">
              {items.filter((i) => i.year === year).map((img) => (
                <button key={img.id} data-testid={`gallery-${img.id}`} onClick={() => setActive(img)} className="block w-full overflow-hidden rounded-2xl group relative">
                  <img src={resolveUrl(img.image_url)} alt={img.caption} className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  {img.caption && (
                    <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent text-white text-sm font-medium px-4 py-3 text-left opacity-0 group-hover:opacity-100 transition-opacity">
                      {img.caption}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </section>
        ))}
        {items.length === 0 && <p className="mt-10 text-slate-500">Photos will appear here after the event.</p>}
      </div>

      {active && (
        <div className="fixed inset-0 z-[60] bg-slate-950/90 grid place-items-center p-5" onClick={() => setActive(null)}>
          <div className="max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <img src={resolveUrl(active.image_url)} alt={active.caption} className="rounded-2xl max-h-[80vh] w-auto" />
            {active.caption && <p className="mt-3 text-center text-white/80">{active.caption} {active.uploaded_by && <span className="text-white/50">· {active.uploaded_by}</span>}</p>}
          </div>
        </div>
      )}

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />}
    </PageShell>
  );
}

function UploadModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [year, setYear] = useState(2026);
  const [busy, setBusy] = useState(false);

  const pick = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) { toast.error("Choose a photo first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      fd.append("year", String(year));
      await api.post("/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added — thank you!");
      onDone();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-950/70 backdrop-blur-sm grid place-items-center p-5" onClick={onClose}>
      <div data-testid="upload-modal" className="w-full max-w-md bg-white rounded-3xl p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl text-slate-900">Add a photo</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X /></button>
        </div>

        <label className="mt-5 block cursor-pointer">
          <input data-testid="upload-file-input" type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
          {preview ? (
            <img src={preview} alt="preview" className="w-full h-56 object-cover rounded-2xl" />
          ) : (
            <div className="w-full h-56 rounded-2xl border-2 border-dashed border-slate-200 grid place-items-center text-slate-400 hover:border-sky-400 hover:text-sky-500 transition-colors">
              <div className="text-center">
                <ImagePlus className="mx-auto mb-2" />
                <span className="text-sm font-medium">Tap to choose a photo</span>
              </div>
            </div>
          )}
        </label>

        <input data-testid="upload-caption" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)" className="mt-4 w-full rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-sky-500" />
        <input data-testid="upload-year" type="number" value={year} onChange={(e) => setYear(e.target.value)} placeholder="Year" className="mt-3 w-full rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-sky-500" />

        <button data-testid="upload-submit" onClick={submit} disabled={busy} className="mt-5 w-full py-3 rounded-full bg-green-600 text-white font-semibold hover:-translate-y-0.5 transition-all disabled:opacity-60">
          {busy ? "Uploading…" : "Upload photo"}
        </button>
      </div>
    </div>
  );
}
